import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import UniversalSearch from './UniversalSearch';
import NavIcon from './NavIcon';

const NAV_GROUPS = [
  { items: [{ to: '/', label: 'Dashboard', icon: 'dashboard' }] },
  {
    items: [
      { to: '/ap-invoices', label: 'AP Invoices', icon: 'apInvoice' },
      { to: '/timesheets', label: 'Timesheets', icon: 'timesheet' },
      { to: '/ap-payments', label: 'AP Payments', icon: 'payments' },
      { to: '/vendor-remittances', label: 'Vendor Remittances', icon: 'remittance' },
      { to: '/purchase-orders', label: 'Purchase Orders', icon: 'purchaseOrder' },
      { to: '/ar-invoices', label: 'AR Invoices', icon: 'arInvoice' },
      { to: '/work-orders', label: 'Work Orders', icon: 'workOrder' },
    ],
  },
  {
    items: [
      { to: '/clients', label: 'Clients', icon: 'client' },
      { to: '/vendors', label: 'Vendors', icon: 'vendor' },
      { to: '/contractors', label: 'Contractors', icon: 'contractor' },
      { to: '/people', label: 'People', icon: 'people' },
    ],
  },
  {
    items: [
      { to: '/expenses', label: 'Expenses', icon: 'expenses' },
    ],
  },
  {
    items: [
      { to: '/reconciliation', label: 'Bank Reconciliation', icon: 'bank' },
      { to: '/ledgers', label: 'Ledgers', icon: 'ledger' },
      { to: '/aging-report', label: 'Aging Report', icon: 'aging' },
      { to: '/settings', label: 'Settings', icon: 'settings' },
      { to: '/audit-log', label: 'Audit Log', icon: 'audit' },
    ],
  },
  {
    items: [
      { to: '/help', label: 'Help / Getting Started', icon: 'help' },
    ],
  },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close the mobile drawer automatically whenever the route changes, so
  // tapping a nav link doesn't leave the drawer sitting open over the new page.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="app-shell">
      {mobileMenuOpen && <div className="sidebar-backdrop" onClick={() => setMobileMenuOpen(false)} />}
      <aside className={'app-sidebar' + (mobileMenuOpen ? ' mobile-open' : '')}>
        <div className="sidebar-brand">
          <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)', letterSpacing: '0.06em' }}>
            P.A.R.A.M
          </span>
          <span className="sidebar-brand-label">Meticulous AR and AP utility for TMV</span>
        </div>
        <nav className="sidebar-nav">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && <div className="sidebar-divider" />}
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
                >
                  {item.icon && <NavIcon name={item.icon} />}
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>{user?.role}</div>
          <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => { logout(); navigate('/login'); }}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="app-shell-main-col">
        <header className="app-header">
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span />
            <span />
            <span />
          </button>
          <UniversalSearch />
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <span className="theme-toggle-knob" />
          </button>
        </header>

        <main className="app-main">{children}</main>
      </div>
    </div>
  );
}
